import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

const article: Article = {
  slug: 'how-to-price-products-in-telegram-stars',
  title: 'How to Price a Product in Telegram Stars',
  metaTitle: 'How to Price a Product in Telegram Stars',
  metaDescription:
    'Turn a dollar price into a Stars amount buyers will actually pay: pack sizes, the seller rate near $0.013, the 3% fee, and prices that sit under a top-up.',
  keywords: [
    'telegram stars price',
    'how many telegram stars',
    'telegram stars pricing',
    'convert usd to telegram stars',
    'what to charge in telegram stars',
    'telegram stars product price',
  ],
  category: 'Business',
  datePublished: '2026-10-20',
  dateModified: '2026-10-20',
  readingTimeMin: 7,
  excerpt:
    'A Stars price is not a dollar price with a different symbol. Buyers purchase Stars in packs, sellers convert Stars to TON at another rate, and a price that falls between two packs quietly kills conversion. Here is a way to pick a number.',
  keyTakeaways: [
    'Start from what you want to keep in dollars, not from what the buyer pays in the app store.',
    'Sellers have historically received about $0.013 per Star before a platform fee. A 3% fee on this platform means you need slightly more Stars to hit the same net.',
    'Round to amounts people can pay with one pack: 50, 100, 150, 250, 500, 1,000, 2,500.',
    'Put the Stars amount in the title area of your post as well as on the payment sheet. Do not make people do the conversion.',
  ],
  body: [
    h2('Two prices, and only one is yours'),
    p(
      'The buyer’s price and your revenue are different numbers. In the Telegram apps, Stars often cost about **$0.015 to $0.025** each, because the store commission is inside the pack price. When you withdraw, Fragment has historically converted Stars at about **$0.013** each. You do not receive the buyer’s pack price.',
    ),
    p(
      'Price the product from the seller rate. If you want about $13 before fees, that is roughly **1,000 Stars**, not “whatever pack costs the buyer $13”. The live rate moves with TON. Recheck it in [Telegram Stars price in USD and TON](/blog/telegram-stars-price-usd-ton) when you reprice.',
    ),

    h2('A formula you can do on paper'),
    ol([
      'Write the amount you want to keep, in dollars. Example: $10.',
      'Divide by 0.013. 10 / 0.013 ≈ 769 Stars.',
      'Add the platform fee if you want $10 after it. On Telegram Stars Payment Platform the fee is 3%, so divide by 0.97 as well: 769 / 0.97 ≈ 793 Stars.',
      'Round up to a pack-friendly price: **1,000 Stars**, or down to **500** if $6–7 net is acceptable.',
    ]),
    callout(
      'Automatic subscriptions cannot exceed 10,000 Stars per 30 days. If the formula lands above that, use a manual period or a one-off invoice. See [subscriptions](/blog/telegram-stars-subscriptions).',
    ),

    h2('Pack-friendly prices'),
    table(
      ['Stars you charge', 'Buyer, rough in-app cost', 'You keep before the 3% fee, at $0.013'],
      [
        ['50', '$1–1.50', '≈ $0.65'],
        ['100', '$2–2.50', '≈ $1.30'],
        ['250', '$4–6', '≈ $3.25'],
        ['500', '$8–12', '≈ $6.50'],
        ['1,000', '$15–25', '≈ $13'],
        ['2,500', '$40–60', '≈ $32'],
      ],
    ),
    p(
      'These columns are ranges, not quotes. Country prices in the app stores differ, and the TON conversion changes. Use the table to avoid a price like 430 Stars, which is hard to pay with a single pack and hard for you to remember.',
    ),

    h2('What to round toward'),
    ul([
      '**Impulse** (a tip, a single essay, a sticker pack): 50 or 100. Friction matters more than the extra dollar.',
      '**A clear product** (ebook, template, preset): 250 or 500. Enough that the file feels paid, low enough for one top-up.',
      '**A month of access**: 250 to 1,000. Say “per 30 days” next to the number so it is not read as a one-off.',
      '**A freelance session**: 1,000 to 2,500, then a second invoice if the work continues. One giant link is harder to pay and harder to refund in part.',
    ]),

    h2('Show the price before the sheet'),
    p(
      'The payment sheet repeats your title, description and Stars amount. People still hesitate if the channel post said “link in bio” and the sheet is the first time they see 1,000. Write the number in the post: “500 Stars, PDF today”. If you also know a rough dollar figure, add it as a hint (“about $8”), and make clear the charge is in Stars.',
    ),
    p(
      'The worked fee stack, including what is store commission and what is the 3% platform fee, is in [Telegram Stars fees explained](/blog/telegram-stars-fees-explained).',
    ),
  ],
  faq: [
    {
      q: 'Can I show a price in dollars on the invoice?',
      a: 'The invoice amount is Stars only. You can mention an approximate dollar value in your post or in the description, but the sheet will charge Stars.',
    },
    {
      q: 'Should I price at 999 Stars instead of 1,000?',
      a: 'No. Stars are whole numbers and buyers think in packs. 1,000 is easier to pay and easier to compare than 999.',
    },
    {
      q: 'Do I absorb the 3% or add it on top?',
      a: 'Add it into the Stars price if you care about a net dollar amount. The fee is 3% of Stars at withdrawal, so a slightly higher sticker price is the simple fix.',
    },
    {
      q: 'How often should I change prices?',
      a: 'When the TON rate has moved enough that your net is wrong, or when conversion on the link is weak. Use a redirect slug so the public URL stays the same.',
    },
  ],
  relatedSlugs: [
    'telegram-stars-price-usd-ton',
    'telegram-stars-fees-explained',
    'telegram-stars-subscriptions',
    'sell-digital-products-telegram-stars',
  ],
};

export default article;
